import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Card, useAccordionButton, Stack, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Link } from 'react-router-dom';

import { tGender, UserLess } from '@models/User';
import { formatDate } from '@utils/datetime';
import { AmountWithDiscount } from '@components/Base';
import { InsurancePolicyUserList } from '@models/InsurancePolicyUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencil, faEye } from '@fortawesome/free-solid-svg-icons';
import { InsurancePolicy } from '@models/InsurancePolicy';
import { selectIsInsurer } from '@components/Auth';
import {
  routeUserDashboardInsurancePolicyUserDetailsPage,
  routeUserDashboardInsurancePolicyClaim,
} from '@components/UserDashboardRoute/route';
import { UserFindModal } from '@components/Feature/User';
import { compact } from 'lodash';
import { InsurancePolicyBadge } from '@components/Feature/InsurancePolicy';
import { InsurancePolicyStatuses } from '@models/InsurancePolicy';
import { InsurancePolicyUserFormModal, InsurancePolicyUserFormValues } from './insurance-policy-user-form-modal';
import { selectFeatureInsurancePolicyUser } from './selector';
import {
  insurancePolicyUserCreate,
  InsurancePolicyUserCreatePayload,
  insurancePolicyUsersFetch,
  InsurancePolicyUsersFetchPayload,
  insurancePolicyUserUpdate,
  InsurancePolicyUserUpdatePayload,
  stateClear,
  stateUpdate,
  StateUpdatePayload,
} from './action';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<InsurancePolicy, 'id' | 'effectiveDate' | 'expirationDate' | 'benefitTemplates'> & {
  canCreate?: boolean;
  canEdit?: boolean;
  canClaim?: boolean;
};

const InsurancePolicyUserCardBody = ({
  id: insurancePolicyId,
  effectiveDate,
  expirationDate,
  benefitTemplates,
  canCreate = false,
  canEdit = false,
  canClaim = false,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isInsurer = useSelector(selectIsInsurer);
  const { policyUsers, isLoading, isReload, isSuccess, pagination } = useSelector(selectFeatureInsurancePolicyUser);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showUserFindDialog, setShowUserFindDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserLess | null>(null);
  const [selectedPolicyUser, setSelectedPolicyUser] = useState<InsurancePolicyUserList | null>(null);
  const [memberSearch, setMemberSearch] = useState('');

  const columns = useMemo(() => compact<TableColumn<InsurancePolicyUserList>>([
    {
      cell: (policyUser) => (
        <Stack direction="horizontal" gap={1}>
          {canEdit && (
            <Button
              onClick={() => handleEdit(policyUser)}
              variant="outline-primary"
              size="sm"
            >
              <FontAwesomeIcon icon={faPencil} />
            </Button>
          )}

          <Button
            as={Link}
            to={routeUserDashboardInsurancePolicyUserDetailsPage({ insurancePolicyUserId: policyUser.id })}
            variant="outline-info"
            size="sm"
          >
            <FontAwesomeIcon icon={faEye} />
          </Button>

          {((canClaim && !policyUser.ended) || policyUser.withinClaimSubmissionDeadline) && (
            <Button
              as={Link}
              to={routeUserDashboardInsurancePolicyClaim({ insurancePolicyId, insurancePolicyUserId: policyUser.id })}
              variant="outline-primary"
              size="sm"
            >
              {t('common:claim')}
            </Button>
          )}
        </Stack>
      ),
      width: '160px',
      button: 'true',
      center: 'true',
    },
    {
      name: t('common:memberId'),
      selector: ({ memberId }) => memberId,
      width: '140px',
    },
    {
      name: t('common:insurerMemberId'),
      selector: ({ insurerMemberId }) => insurerMemberId,
      width: '140px',
    },
    {
      name: t('common:staffId'),
      selector: ({ staffId }) => staffId ?? '',
      width: '140px',
    },
    {
      name: t('user.name'),
      selector: ({ user }) => user.name,
      width: '120px',
    },
    {
      name: t('user.dob'),
      selector: ({ user }) => formatDate(user.dob),
      width: '120px',
    },
    {
      name: t('user.gender'),
      selector: ({ user }) => tGender(user.gender),
    },
    {
      name: t('common:primaryInsured'),
      selector: ({ familyHead }) => familyHead?.name,
      width: '150px',
    },
    {
      name: t('user.occupation'),
      selector: ({ user }) => user.occupation,
    },
    !isInsurer && {
      name: t('user.email'),
      selector: ({ user }) => user.email,
      width: '180',
    },
    {
      name: t('common:effectiveDate'),
      selector: (policyUser) => formatDate(policyUser.effectiveDate),
      width: '120px',
    },
    {
      name: t('common:expirationDate'),
      selector: (policyUser) => formatDate(policyUser.expirationDate),
      width: '120px',
    },
    {
      name: t('common:plan'),
      selector: ({ benefitTemplate }) => benefitTemplate.name,
      width: '120px',
    },
    {
      name: t('common:status'),
      cell: (policyUser) => (
        <InsurancePolicyBadge
          status={policyUser.ended ? InsurancePolicyStatuses.Ended : InsurancePolicyStatuses.Active}
        />
      ),
    },
    {
      name: t('common:premium'),
      selector: ({ premium }) => <AmountWithDiscount skipIncluded finalAmount={premium} />,
    },
    {
      name: t('common:refundedPremium'),
      selector: ({ refundedPremium }) => <AmountWithDiscount skipIncluded finalAmount={refundedPremium} />,
    },
  ]), [insurancePolicyId, canClaim]);

  const handleClose = () => {
    setSelectedPolicyUser(null);
    setShowFormDialog(false);
    const payload: StateUpdatePayload = { isSuccess: false, serverErrors: {} };
    dispatch(stateUpdate(payload));
  };

  const handleEdit = (policyUser: InsurancePolicyUserList) => {
    setSelectedPolicyUser(policyUser);
    setShowFormDialog(true);
  };

  useEffect(() => {
    if (isSuccess && showFormDialog) {
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    const payload: InsurancePolicyUsersFetchPayload = { insurancePolicyId, page, perPage, search: memberSearch };
    dispatch(insurancePolicyUsersFetch(payload));
  }, [insurancePolicyId, page, perPage, memberSearch]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: InsurancePolicyUsersFetchPayload = { insurancePolicyId, page: 1, perPage };
    dispatch(insurancePolicyUsersFetch(payload));
  }, [isReload]);

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {pluralize(t('common:member'), 2)} ({pagination?.total ?? 0})
            </Toggle>
            {canCreate && (
              <Button onClick={() => setShowUserFindDialog(true)} variant="outline-primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t('common:add')}
              </Button>
            )}
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <>
              <FormControl
                className="mb-3"
                placeholder={t('common:searchByMemberIdOrName')}
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              <DataTable
                columns={columns}
                data={policyUsers ?? []}
                customStyles={customStyles}
                fixedHeader
                pagination
                paginationServer
                progressPending={isLoading}
                paginationPerPage={perPage}
                paginationTotalRows={pagination?.total ?? 0}
                onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
                onChangePage={(newPage) => setPage(newPage)}
              />
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      {(canCreate || canEdit) && (
        <InsurancePolicyUserFormModal
          benefitTemplates={benefitTemplates}
          effectiveDate={effectiveDate}
          expirationDate={expirationDate}
          user={selectedPolicyUser?.user || selectedUser}
          policyUser={selectedPolicyUser}
          show={showFormDialog}
          onHide={handleClose}
          onSubmit={(values: InsurancePolicyUserFormValues) => {
            if (selectedPolicyUser) {
              const payload: InsurancePolicyUserUpdatePayload = { id: selectedPolicyUser.id, ...values };
              dispatch(insurancePolicyUserUpdate(payload));
            } else {
              if (selectedUser) {
                const payload: InsurancePolicyUserCreatePayload = {
                  userId: selectedUser.id,
                  insurancePolicyId,
                  ...values,
                };
                dispatch(insurancePolicyUserCreate(payload));
              }
            }
          }}
        />
      )}

      {(canCreate || canEdit) && (
        <UserFindModal
          show={showUserFindDialog}
          onHide={() => {
            setSelectedUser(null);
            setShowUserFindDialog(false);
          }}
          onSuccess={(user) => {
            setSelectedUser(user);
            setShowUserFindDialog(false);
            setShowFormDialog(true);
          }}
        />
      )}
    </>
  );
};

export { InsurancePolicyUserCardBody };
